import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import Link from '@base/Link/Link';
import LinearProgressBar from '@base/LinearProgressBar/LinearProgressBar';
import Tags from '@base/Tags/Tags';
import AddToList from '@icon/AddToList';
import AddedToList from '@icon/AddedToList';
import Lock from '@icon/Lock';
import OfflineClassIcon from '@icon/OfflineClassIcon';
import { isSameDay, getTime } from '@utils/dates';
import { getDefaultThumbnail, getLink, getLinkPath } from '@models/Course';
import useSubscribed from '@hooks/useSubscribed';
import segmentStorage from '@events/segmentStorage';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { mediaQuery, Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUid } from '@api/selectors/auth';
import { wordsToSentence, getCommaSeparatedUIDs } from '@utils/textHelper';
import { evtNameCategoryMap, programmeTypes } from '@constants/index';
import { SEARCH_RESULT_CLICKED, SEARCH_RESULT_PAGE } from '@constants/segment';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { getIsSubscribedToLite } from '@api/selectors/subscription';
import { updateRecentSearchLog } from '@api/fetchers/search/search';
import { StyledTag, EnrollButton, Container } from '../CourseCard';
import PlusCourseCardDetails from './PlusCourseCardDetails';
import CoursePlaceholder from './placeholderCourseThumbail/CoursePlaceholder';
import SvgThumbail from './placeholderCourseThumbail/SvgThumbnail';

const SvgCourseThumbnail = styled(SvgThumbail)`
  width: 272px;
  height: ${({ $orientation }) =>
    $orientation === 'horizontal' ? '152px' : 'auto'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const CourseThumbnail = styled(Thumbnail)`
  width: 272px;
  height: ${({ orientation }) => orientation === 'horizontal' && '152px'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const CardContainer = styled(Container)`
  display: flex;
  flex: 1;
  overflow: hidden;
  height: 100%;
  flex-direction: ${({ orientation }) =>
    orientation === 'horizontal' ? 'row' : 'column'};
  @media only screen and (max-width: 47.95em) {
    flex: auto;
    box-shadow: ${boxShadowGenerator({
      blur: Spacings.SPACING_4B
    })};
  }
`;

const PlaceholderThumbnail = styled(CoursePlaceholder)`
  height: 100%;
  width: 100%;
  transform: scale(1.02);
  background-color: var(--color-divider);
  object-fit: cover;
  @media only screen and (max-width: 47.95em) {
    transform: scale(1);
  }
`;

const DefaultThumbnail = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${Spacings.SPACING_2B};
`;

const ProgressTag = styled(Tags)`
  background-color: var(--color-i-black);
  color: var(--color-i-white);
  margin-right: ${Spacings.SPACING_2B};
`;

const Tag = styled(StyledTag)`
  position: static;
`;

const StyledProgressbar = styled(LinearProgressBar)`
  &.MuiLinearProgress-root {
    height: ${Spacings.SPACING_6};
    border-radius: ${Spacings.SPACING_2B};
    border-top-left-radius: 0;
  }
`;

const CourseProgressbar = styled(StyledProgressbar)`
  position: absolute;
  left: 0px;
  bottom: 0px;
`;

const TagsWrapper = styled.div`
  position: absolute;
  left: ${Spacings.SPACING_4B};
  bottom: ${Spacings.SPACING_14};
`;

const getTopicName = (topicGroups, topic) => {
  let topicName = topicGroups?.[0]?.name || topicGroups?.[0]?.title || '';

  if (topic && topic.uid) {
    topicName = topic.name;
  }
  return topicName;
};

const PlusCourseCard = ({
  gridColumn,
  orientation,
  className,
  course,
  toggleEnroll,
  cardType,
  topic,
  type,
  imageBorderRadius,
  showProgress,
  dataAnalytics,
  index,
  label,
  lastPrimarySource,
  searchData,
  disabledTopicGroup,
  hideLessonCount,
  onCardClick
}) => {
  const { authors } = course;
  const isTestSeries = course.courseType === programmeTypes.TESTSERIES;
  const goalUID = course?.goalUid || course?.goal?.uid;
  const { pathname } = useRouter();
  const isSubscribed = useSubscribed(goalUID);
  const isSubscribedToLite = useMyInfo(getIsSubscribedToLite(goalUID));
  const isEnrolledInCourse = course.isEnrolled || false;
  const isTestSeriesAndEnrolledPage =
    pathname.includes('enrolled') && isTestSeries && !course.isPartOfBatch;
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const goalSegmentData = useGoalEventProps({ goalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course
  });
  const { topicGroups } = course;
  const topicName = getTopicName(topicGroups, topic);
  const { color } = getTopicGroupAssetObj(topicName);
  const meUID = useMyInfo(getMeUid);

  const coverPhoto =
    course.coverPhotoV1 ||
    course.coverPhoto ||
    course.thumbnail ||
    getDefaultThumbnail(course);

  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const renderIconButton = useMemo(() => {
    let EnrollButtonIcon = null;
    if (isSubscribedToLite) {
      if (isTestSeriesAndEnrolledPage && isEnrolledInCourse) {
        EnrollButtonIcon = AddedToList;
      } else if (!isEnrolledInCourse && isTestSeriesAndEnrolledPage) {
        EnrollButtonIcon = AddToList;
      } else {
        EnrollButtonIcon = Lock;
      }
    } else if (!isSubscribed) {
      EnrollButtonIcon = Lock;
    } else if (isEnrolledInCourse) {
      EnrollButtonIcon = AddedToList;
    } else {
      EnrollButtonIcon = AddToList;
    }
    return <EnrollButtonIcon size={Spacings.SPACING_5B} />;
  }, [
    isEnrolledInCourse,
    isSubscribed,
    isSubscribedToLite,
    isTestSeriesAndEnrolledPage
  ]);

  if (!course) return null;

  const { isLive, isOngoing } = course;
  const nextSession = course?.nextSession || {};
  const { isOffline = false, isLiveStreamingEnabled = true } =
    nextSession?.properties || {};
  const isStreamingDisabled = isOffline && !isLiveStreamingEnabled;
  const handleEnroll = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleEnroll(
      course.uid,
      isEnrolledInCourse,
      {
        ...goalSegmentData,
        ...segmentData
      },
      isRecordedGoal
    );
  };

  const ThumbnailTags = () => {
    const showProgressbar =
      isLoggedIn && isSubscribed && course.itemCount > 0 && showProgress;
    const showLiveAtTag =
      isOngoing &&
      !isLive &&
      isSameDay(new Date(course.nextTrack?.liveAtString), new Date());
    const showEnrollButton =
      !course.showAsLesson &&
      !course.isSpecial &&
      (course.forPlus || type === 'syllabus') &&
      !!toggleEnroll;

    return (
      <>
        {showEnrollButton && (
          <EnrollButton
            onClick={
              isSubscribed ||
              (isSubscribedToLite && isTestSeriesAndEnrolledPage)
                ? handleEnroll
                : undefined
            }
            iconProps={{ hovDisabled: true }}
            isEnrolled={isEnrolledInCourse}
            disableHover={!isSubscribed}
            dataAnalytics={`${dataAnalytics}-courseEnroll`}
          >
            {renderIconButton}
          </EnrollButton>
        )}
        {!showProgressbar && showLiveAtTag && course.nextTrack.liveAtString && (
          <StyledTag type="filled">
            {getTime(course.nextTrack.liveAtString)}
          </StyledTag>
        )}
        {!showProgressbar && isLive && (
          <StyledTag type="live" $isStreamingDisabled={isStreamingDisabled}>
            {isStreamingDisabled ? (
              <>
                <OfflineClassIcon height={16} width={16} /> Live at Centre
              </>
            ) : (
              'Live'
            )}
          </StyledTag>
        )}
        {showProgressbar && (
          <>
            <CourseProgressbar
              width="100%"
              value={(course.lessonsCompleted / course.itemCount) * 100}
              backgroundColor="var(--color-overlay)"
              overlayColor="var(--color-i-green)"
            />
            <TagsWrapper>
              <ProgressTag type="filled">{`${course.lessonsCompleted} / ${course.itemCount} COMPLETED`}</ProgressTag>
              {showLiveAtTag && course.nextTrack.liveAtString && (
                <Tag type="filled">
                  {getTime(course.nextTrack.liveAtString)}
                </Tag>
              )}
              {isLive && <Tag type="live">Live</Tag>}
            </TagsWrapper>
          </>
        )}
      </>
    );
  };
  const handleAnalytics = () => {
    const courseData = course || {};
    const courseUID = courseData.uid || '';
    // Means card was rendered on a search page
    if (course.showAsLesson) {
      segment.track('Lesson Viewed', {
        last_primary_source: lastPrimarySource,
        last_primary_source_section: label
      });
    }
    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      // send segment search result clicked event
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          sub_position: index + 1,
          is_object_plus: courseData.forPlus,
          is_object_special: courseData.isSpecial,
          educator_uid: getCommaSeparatedUIDs(authors),
          educator_username: getCommaSeparatedUIDs(authors, 'username'),
          educator_name: getCommaSeparatedUIDs(authors, 'name'),
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Courses',
          search_section: 'Global',
          current_screen: 'Courses',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${courseUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      updateRecentSearchLog({
        ...searchData,
        entity_id: `${searchData.object_type}:${courseUID}`,
        goal_uid: goalUID
      });
    }
    const info = {
      index,
      'Last Primary Source': lastPrimarySource
    };
    info['Last Primary Source Section'] =
      label || segmentStorage.getItem('lpss');
    if (lastPrimarySource === 'lesson page') {
      segment.track(course.isSpecial ? 'CLX Lesson View' : 'Course Viewed', {
        last_primary_source: lastPrimarySource,
        last_primary_source_section: label,
        course_uid: course.uid,
        course_title: course.name,
        start_date: course.startsAt,
        end_date: course.endsAt,
        educator_uid: wordsToSentence(course.authors, 'uid', ',', ','),
        educator_username: wordsToSentence(
          course.authors,
          'username',
          ',',
          ','
        ),
        is_offline_course:
          courseData?.courseType === programmeTypes.HYBRID_COURSE,
        ...goalSegmentData
      });
    }

    // Adding LPSS to segment from label property and deleting it on class exit
    // in case property is not deleted removing it if label is not passed
    if (label) segmentStorage.setItem('lpss', label);
    else segmentStorage.deleteItem('lpss');
    onCardClick();
  };
  return (
    <CardContainer
      className={className}
      gridColumn={gridColumn}
      orientation={orientation} // making it transient prop will break UI in courses page
    >
      <Link
        href={getLinkPath(course)}
        as={getLink(course)}
        onClick={handleAnalytics}
        className="card-link"
      >
        <>
          {coverPhoto ? (
            <CourseThumbnail
              width={272}
              height={152}
              layout="intrinsic"
              alt={course.newThumbnail ? 'new-thumbnail' : 'thumbnail'}
              imgUrl={coverPhoto}
              orientation={orientation}
              borderRadius={imageBorderRadius}
              data-analytics={`${dataAnalytics}-courseCard`}
            >
              <ThumbnailTags />
            </CourseThumbnail>
          ) : (
            <SvgCourseThumbnail
              as={DefaultThumbnail}
              $orientation={orientation}
            >
              <PlaceholderThumbnail color={color} />
              <ThumbnailTags />
            </SvgCourseThumbnail>
          )}
        </>
      </Link>
      <PlusCourseCardDetails
        handleAnalytics={handleAnalytics}
        orientation={orientation}
        course={course}
        cardType={cardType}
        topic={topic}
        searchData={searchData}
        type={type}
        disabledTopicGroup={disabledTopicGroup}
        hideLessonCount={hideLessonCount}
      />
    </CardContainer>
  );
};

export default PlusCourseCard;

PlusCourseCard.propTypes = {
  gridColumn: PropTypes.string,
  className: PropTypes.string,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  course: PropTypes.objectOf(PropTypes.unknown).isRequired,
  toggleEnroll: PropTypes.func.isRequired,
  cardType: PropTypes.string,
  topic: PropTypes.objectOf(PropTypes.unknown),
  type: PropTypes.string,
  imageBorderRadius: PropTypes.string,
  showProgress: PropTypes.bool,
  dataAnalytics: PropTypes.string,
  index: PropTypes.number.isRequired,
  label: PropTypes.string,
  lastPrimarySource: PropTypes.string,
  searchData: PropTypes.objectOf(PropTypes.unknown),
  disabledTopicGroup: PropTypes.bool,
  isEnrolled: PropTypes.bool,
  hideLessonCount: PropTypes.bool,
  onCardClick: PropTypes.func
};

PlusCourseCard.defaultProps = {
  gridColumn: 'span 6',
  className: '',
  orientation: 'vertical',
  cardType: 'all_cards',
  topic: {},
  type: '',
  imageBorderRadius: '0px',
  showProgress: false,
  dataAnalytics: '',
  lastPrimarySource: '',
  label: '',
  searchData: {},
  disabledTopicGroup: false,
  isEnrolled: false,
  hideLessonCount: false,
  onCardClick: () => {}
};
